import { useSearchParams } from 'react-router-dom';
import type { ComponentType } from 'react';
import {
  MdDescription,
  MdLocalOffer,
  MdShoppingCart,
  MdSync,
  MdReportProblem,
  MdShoppingBag,
  MdArchive,
} from 'react-icons/md';
import { erpPageTabsClass, erpPageTabClasses } from '@euroasiann/shared/ui';
import { cn } from '../../lib/utils';
import { RFQsPage } from '../RFQs/RFQsPage';
import { YourRFQDetailsPage } from '../RFQs/YourRFQDetailsPage';
import { OffersPage } from '../Offers/OffersPage';
import { POListPage } from '../PO/POListPage';
import { ClaimRequestsPage } from '../ClaimRequests/ClaimRequestsPage';
import {
  usePipelineOverviewCounts,
  type PipelineOverviewCounts,
} from '../../hooks/usePipelineOverviewCounts';
import { CompletedOrdersPage } from '../CompletedOrders/CompletedOrdersPage';

type PurchasesTab =
  | 'overview'
  | 'rfqs'
  | 'offers'
  | 'purchase-orders'
  | 'in-progress'
  | 'claims'
  | 'order-history';

const PURCHASES_TAB_PARAM = 'purchasesTab';
const RFQ_ID_PARAM = 'rfqId';

const PURCHASES_TABS = [
  { id: 'overview' as const, label: 'Overview', icon: MdShoppingBag },
  { id: 'rfqs' as const, label: 'RFQ', icon: MdDescription },
  { id: 'offers' as const, label: 'Offers', icon: MdLocalOffer },
  { id: 'purchase-orders' as const, label: 'Purchase Orders', icon: MdShoppingCart },
  { id: 'in-progress' as const, label: 'In Progress', icon: MdSync },
  { id: 'claims' as const, label: 'Claims', icon: MdReportProblem },
  { id: 'order-history' as const, label: 'Order History', icon: MdArchive },
];

const PURCHASES_RFQ_LIST_PATH = '/dashboard/admin/purchases?purchasesTab=rfqs';

const COUNT_BY_TAB: Record<
  Exclude<PurchasesTab, 'overview'>,
  keyof PipelineOverviewCounts
> = {
  rfqs: 'rfqs',
  offers: 'offers',
  'purchase-orders': 'purchaseOrders',
  'in-progress': 'inProgress',
  claims: 'claims',
  'order-history': 'completedOrders',
};

function parsePurchasesTab(value: string | null): PurchasesTab {
  if (value === 'orders-delivered' || value === 'completed-orders') return 'order-history';
  if (value && PURCHASES_TABS.some((tab) => tab.id === value)) {
    return value as PurchasesTab;
  }
  return 'overview';
}

function formatCount(value: number, loading: boolean): string {
  if (loading) return '—';
  return value.toLocaleString();
}

export function PurchasesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parsePurchasesTab(searchParams.get(PURCHASES_TAB_PARAM));
  const rfqId = searchParams.get(RFQ_ID_PARAM);
  const { counts, isLoading: countsLoading } = usePipelineOverviewCounts(
    'purchases',
    activeTab === 'overview'
  );

  const setTab = (tab: PurchasesTab) => {
    const next = new URLSearchParams();
    if (tab !== 'overview') {
      next.set(PURCHASES_TAB_PARAM, tab);
    }
    setSearchParams(next, { replace: true });
  };

  const showRfqDetail = activeTab === 'rfqs' && Boolean(rfqId);

  return (
    <div className="w-full min-h-screen bg-[hsl(var(--background))] p-4 lg:p-8 font-sans text-[hsl(var(--foreground))]">
      <div className={cn(erpPageTabsClass, 'overflow-x-auto mb-6')}>
        {PURCHASES_TABS.map((tab) => {
          const Icon = tab.icon as ComponentType<{ className?: string }>;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setTab(tab.id)}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                erpPageTabClasses(isActive),
                'inline-flex items-center gap-2 whitespace-nowrap'
              )}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'rfqs' && showRfqDetail && (
        <YourRFQDetailsPage rfqId={rfqId!} embedded backTo={PURCHASES_RFQ_LIST_PATH} />
      )}
      {activeTab === 'rfqs' && !showRfqDetail && (
        <RFQsPage embedded pipeline="purchases" />
      )}
      {activeTab === 'offers' && <OffersPage embedded pipeline="purchases" />}
      {activeTab === 'purchase-orders' && (
        <POListPage embedded direction="sent" statusScope="open" />
      )}
      {activeTab === 'in-progress' && (
        <POListPage embedded direction="sent" statusScope="in-progress" />
      )}
      {activeTab === 'claims' && <ClaimRequestsPage embedded mode="created" />}
      {activeTab === 'order-history' && (
        <CompletedOrdersPage embedded pipeline="purchases" />
      )}
      {activeTab === 'overview' && (
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 lg:p-8 shadow-sm space-y-6">
          <div>
            <h2 className="text-xl font-bold text-[hsl(var(--foreground))]">Purchases pipeline</h2>
            <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
              Raise RFQ to vendors → receive offers → send purchase order → proforma → delivery.
            </p>
          </div>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {(
              [
                {
                  tab: 'rfqs' as const,
                  title: 'RFQ',
                  desc: 'Create an RFQ and select vendors who should quote.',
                },
                {
                  tab: 'offers' as const,
                  title: 'Offers',
                  desc: 'Review quotations received from vendors and finalize one.',
                },
                {
                  tab: 'purchase-orders' as const,
                  title: 'Purchase Orders',
                  desc: 'PO sent to the selected vendor after you finalize their offer.',
                },
                {
                  tab: 'in-progress' as const,
                  title: 'In Progress',
                  desc: 'Vendor proforma, shipping, and fulfilment while the order is open.',
                },
                {
                  tab: 'claims' as const,
                  title: 'Claims',
                  desc: 'Claims you raise against vendor deliveries.',
                },
                {
                  tab: 'order-history' as const,
                  title: 'Order History',
                  desc: 'Closed POs with settled claims — historical analytics.',
                },
              ] as const
            ).map((item) => {
              const count = counts[COUNT_BY_TAB[item.tab]];
              return (
                <div
                  key={item.tab}
                  className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-4"
                >
                  <div className="mb-2 flex items-start justify-between gap-3">
                    <span className="font-semibold text-[hsl(var(--foreground))]">
                      {item.title}
                    </span>
                    <span
                      className="shrink-0 text-2xl font-black tabular-nums leading-none text-[#0088cc]"
                      aria-label={`${item.title} count: ${formatCount(count, countsLoading)}`}
                    >
                      {formatCount(count, countsLoading)}
                    </span>
                  </div>
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
